import { Box, Paper, Stack, Typography } from "@mui/material";
import { LiveCursorLine } from "components/scrapEyeDataDashboard/VideoPlayer/components/LiveCursorLine";
import { VideoTimelineAreaTrack } from "components/scrapEyeDataDashboard/VideoPlayer/components/VideoTimelineAreaTrack";
import { VideoTimelineEvent } from "components/scrapEyeDataDashboard/VideoPlayer/components/VideoTimelineEvent";
import {
  useTimelineWheelZoom,
  useTimelineZoom,
} from "components/scrapEyeDataDashboard/VideoPlayer/contexts/TimelineZoomContext";
import { AreaLabels, EventWarning } from "components/scrapEyeLabelling/types";
import { Dayjs } from "dayjs";
import { useTenantTranslation } from "hooks/formatters";
import { useMemo, useRef } from "react";
import { EventType } from "src/store/api/generatedApi";
import { v4 as uuidv4 } from "uuid";

interface VerifiedEvent extends AreaLabels {
  id: string;
  start: Dayjs;
  end: Dayjs;
  eventType: EventType;
  material: string | null;
  area: string | null;
  warnings: EventWarning[];
}

interface VerifiedEventsTimelineProps {
  verifiedEvents: VerifiedEvent[];
  videoStart: Dayjs;
  currentTime: number;
  duration: number;
}

export const VerifiedEventsTimeline = ({
  verifiedEvents,
  videoStart,
  currentTime,
  duration,
}: VerifiedEventsTimelineProps) => {
  const { t } = useTenantTranslation();
  const zoomContext = useTimelineZoom();
  const timelineRef = useRef<HTMLDivElement>(null);
  const { onMouseMove, onMouseLeave } = useTimelineWheelZoom(
    timelineRef,
    videoStart
  );

  const visibleStart = zoomContext?.visibleStart ?? videoStart;
  const visibleEnd =
    zoomContext?.visibleEnd ?? videoStart.add(duration, "second");
  const visibleDuration = zoomContext?.duration ?? duration;

  const formattedForTimeline = useMemo(() => {
    const events = [...verifiedEvents]
      .sort((a, b) => a.start.valueOf() - b.start.valueOf())
      .map((e) => ({
        id: e.id,
        start: e.start,
        end: e.end,
        eventType: e.eventType,
        material: e.material,
        area: e.area,
        areaLabel: e.areaLabel,
        areaShortLabel: e.areaShortLabel,
        pairId: null as string | null,
        warnings: e.warnings,
      }));

    const pairedEvents = events.map((event, index) => {
      if (event.pairId !== null || event.eventType !== "lift") return event;
      const nextEvent = events[index + 1];
      if (
        nextEvent?.eventType === "drop" &&
        nextEvent.material === event.material &&
        !nextEvent.pairId
      ) {
        return { ...event, pairId: uuidv4() };
      }
      return event;
    });

    return pairedEvents.map((event, index) => {
      if (event.eventType === "drop" && !event.pairId) {
        const prevEvent = pairedEvents[index - 1];
        if (
          prevEvent?.eventType === "lift" &&
          prevEvent.pairId &&
          prevEvent.material === event.material
        ) {
          return { ...event, pairId: prevEvent.pairId };
        }
      }
      return event;
    });
  }, [verifiedEvents]);

  const visibleEvents = useMemo(
    () =>
      formattedForTimeline.filter(
        (event) =>
          event.end.isAfter(visibleStart) && event.start.isBefore(visibleEnd)
      ),
    [formattedForTimeline, visibleStart, visibleEnd]
  );

  if (verifiedEvents.length === 0) {
    return null;
  }

  const TIMELINE_HEIGHT = 24;

  return (
    <Paper
      variant="outlined"
      sx={{ mt: 2, p: 2, bgcolor: "background.default" }}
    >
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ mb: 1.5, display: "block", fontWeight: 600 }}
      >
        {t("verifiedEvents")} ({verifiedEvents.length})
      </Typography>
      <Box
        ref={timelineRef}
        onMouseMove={onMouseMove}
        onMouseLeave={onMouseLeave}
        sx={{ position: "relative", mx: -2 }}
      >
        <LiveCursorLine
          videoStart={videoStart}
          visibleStart={visibleStart}
          visibleDuration={visibleDuration}
          trackRef={timelineRef}
        />

        <Stack spacing={0.5}>
          <VideoTimelineAreaTrack
            events={visibleEvents}
            eventType="lift"
            videoStart={visibleStart}
            duration={visibleDuration}
          />
          <Box
            sx={{
              height: TIMELINE_HEIGHT,
              bgcolor: "action.selected",
              borderRadius: 0.5,
              display: "flex",
              backgroundColor: "action.hover",
              position: "relative",
            }}
          >
            {visibleEvents.map((event, index) => {
              const eventStartSec = event.start.diff(videoStart, "second");
              const eventEndSec = event.end.diff(videoStart, "second");
              return (
                <VideoTimelineEvent
                  key={event.id}
                  event={event}
                  index={index}
                  selectedEventId={null}
                  onEventClick={undefined}
                  allEvents={visibleEvents}
                  videoStart={visibleStart}
                  duration={visibleDuration}
                  colorEventsBy="material"
                  isActive={
                    currentTime >= eventStartSec && currentTime <= eventEndSec
                  }
                />
              );
            })}
          </Box>
          <VideoTimelineAreaTrack
            events={visibleEvents}
            eventType="drop"
            videoStart={visibleStart}
            duration={visibleDuration}
          />
        </Stack>
      </Box>
    </Paper>
  );
};
